import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import ViewAsToggle from './components/ViewAsToggle';
import TestSiteBanner from './components/TestSiteBanner';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './pages/login';
import SetPassword from './pages/SetPassword';
import OAuthAuthorize from './pages/OAuthAuthorize';
import Dashboard from './pages/Dashboard';
import Transactions from './pages/transactions';
import CreateTransaction from './pages/CreateTransaction';
import TransactionDetail from './pages/TransactionDetail';
import PageErrorBoundary from './components/PageErrorBoundary';
import { useParams } from 'react-router-dom';
import Tasks from './pages/tasks';
import OpenHousesPage from './pages/OpenHousesPage';
import NotificationsPage from './pages/NotificationsPage';
import NotificationPreferences from './pages/NotificationPreferences';
import SellerForm from './pages/SellerForm';
import BuyerForm from './pages/BuyerForm';
import TransactionEditRoute from './components/TransactionEditRoute';
import Feedback from './pages/Feedback';
import LandlordForm from './pages/LandlordForm';
import TenantForm from './pages/TenantForm';
import Admin from './pages/admin';
import AgentProfileSettings from './pages/AgentProfileSettings';
import CommissionsPage from './pages/CommissionsPage';
import Training from './pages/Training';
import TrainingHome from './pages/TrainingHome';
import MarketingCenter from './pages/MarketingCenter';
import MarketingToolsPage from './pages/MarketingToolsPage';
import YouTubeStudioPage from './pages/marketing/YouTubeStudio';
import KnowledgeBase from './pages/KnowledgeBase';
import BuildStatus from './pages/BuildStatus';
import ComingSoon from './pages/ComingSoon';
import ToolsHub from './pages/ToolsHub';
import CmaBuilder from './pages/tools/CmaBuilder';
import PocketListings from './pages/tools/PocketListings';
import ToolRoleGate from './components/ToolRoleGate';
import MortgageCalculator from './pages/tools/MortgageCalculator';
import SellerNetSheet from './pages/tools/SellerNetSheet';
import ClosingCostEstimator from './pages/tools/ClosingCostEstimator';
import MyProfile from './pages/MyProfile';
import GoogleCallback from './pages/GoogleCallback';
import AppShell from './components/AppShell';
import CalendarPage from './pages/CalendarPage';
import PrivacyPage from './pages/legal/PrivacyPage';
import TermsPage from './pages/legal/TermsPage';
import MarketUpdate from './pages/tools/MarketUpdate';
import UnderContractForm from './pages/UnderContractForm';
import FirstSignInTour from './components/FirstSignInTour';
import TeamLeadCertAdmin from './pages/TeamLeadCertAdmin';
import TeamLeadCertRoute from './components/training/TeamLeadCertRoute';
import CrmGate from './components/CrmGate';
import KellyOnly from './components/KellyOnly';
import ContactsPage from './pages/crm/ContactsPage';
import DuplicatesPage from './pages/crm/DuplicatesPage';
import SellerInterestsPage from './pages/crm/SellerInterestsPage';
import MyPerformance from './pages/MyPerformance';
import PendingCommission from './pages/PendingCommission';
import EmailBuilderPage from './pages/crm/EmailBuilderPage';
import CampaignsPage from './pages/crm/CampaignsPage';
import ListsPage from './pages/crm/ListsPage';
import MessagesLogPage from './pages/crm/MessagesLogPage';
import InboxPage from './pages/crm/InboxPage';
import PowerDialerPage from './pages/crm/PowerDialerPage';
import './App.css';
import './styles/mobile.css';

// Keyed by deal id, so reopening a deal (or opening another) always starts from a clean page.
const TransactionDetailPage = () => { const { id } = useParams(); return <PageErrorBoundary key={id} title="This transaction hit a problem" backTo="/transactions"><TransactionDetail key={id} /></PageErrorBoundary>; };

function App() {
  return (
    <Router>
      <TestSiteBanner />
      <AuthProvider>
        <ViewAsToggle />
        <FirstSignInTour />
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Login />} />
          <Route path="/login" element={<Login />} />
          <Route path="/oauth/authorize" element={<OAuthAuthorize />} />
          <Route path="/set-password" element={<SetPassword />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />

          {/* Protected Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/commissions"
            element={
              <ProtectedRoute>
                <AppShell><CommissionsPage /></AppShell>
              </ProtectedRoute>
            }
          />

          <Route
            path="/transactions"
            element={
              <ProtectedRoute>
                <AppShell><Transactions /></AppShell>
              </ProtectedRoute>
            }
          />

          <Route
            path="/transactions/:id"
            element={
              <ProtectedRoute>
                <AppShell><TransactionDetailPage /></AppShell>
              </ProtectedRoute>
            }
          />

          <Route
            path="/open-houses"
            element={
              <ProtectedRoute>
                <AppShell><OpenHousesPage /></AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/tasks"
            element={
              <ProtectedRoute>
                <AppShell><Tasks /></AppShell>
              </ProtectedRoute>
            }
          />

          <Route
            path="/notifications"
            element={
              <ProtectedRoute>
                <AppShell><NotificationsPage /></AppShell>
              </ProtectedRoute>
            }
          />

          <Route
            path="/notification-preferences"
            element={
              <ProtectedRoute>
                <AppShell><NotificationPreferences /></AppShell>
              </ProtectedRoute>
            }
          />

          <Route
            path="/transactions/new"
            element={
              <ProtectedRoute>
                <AppShell><CreateTransaction /></AppShell>
              </ProtectedRoute>
            }
          />

          <Route
            path="/transactions/new/seller"
            element={
              <ProtectedRoute>
                <AppShell><SellerForm /></AppShell>
              </ProtectedRoute>
            }
          />

          <Route
            path="/transactions/new/buyer"
            element={
              <ProtectedRoute>
                <AppShell><BuyerForm /></AppShell>
              </ProtectedRoute>
            }
          />

          <Route
            path="/transactions/new/landlord"
            element={
              <ProtectedRoute>
                <AppShell><LandlordForm /></AppShell>
              </ProtectedRoute>
            }
          />

          <Route
            path="/transactions/new/tenant"
            element={
              <ProtectedRoute>
                <AppShell><TenantForm /></AppShell>
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin"
            element={
              <ProtectedRoute requiredRole="Admin">
                <AppShell><Admin /></AppShell>
              </ProtectedRoute>
            }
          />

          <Route path="/google-callback" element={<ProtectedRoute><GoogleCallback /></ProtectedRoute>} />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <AppShell><MyProfile /></AppShell>
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/agent-settings"
            element={
              <ProtectedRoute requiredRole="Admin">
                <AppShell><AgentProfileSettings /></AppShell>
              </ProtectedRoute>
            }
          />

          <Route
            path="/training"
            element={
              <ProtectedRoute>
                <AppShell><TrainingHome /></AppShell>
              </ProtectedRoute>
            }
          />

          <Route path="/marketing-tools" element={<ProtectedRoute><AppShell><MarketingToolsPage /></AppShell></ProtectedRoute>} />
          <Route path="/marketing" element={<ProtectedRoute><AppShell><MarketingCenter /></AppShell></ProtectedRoute>} />
          <Route path="/marketing/youtube-studio" element={<ProtectedRoute><AppShell><YouTubeStudioPage /></AppShell></ProtectedRoute>} />

          {/* Fallback */}
          <Route path="/status" element={<ProtectedRoute><BuildStatus /></ProtectedRoute>} />
          <Route path="/transactions/:id/edit" element={<ProtectedRoute><AppShell><TransactionEditRoute /></AppShell></ProtectedRoute>} />
          <Route path="/transactions/:id/under-contract" element={<ProtectedRoute><AppShell><UnderContractForm /></AppShell></ProtectedRoute>} />
          <Route path="/training/resi-setup" element={<ProtectedRoute><AppShell><Training moduleKey="resiSetup" /></AppShell></ProtectedRoute>} />
          <Route path="/training/how-to-use-resi" element={<ProtectedRoute><AppShell><Training moduleKey="howToUseResi" /></AppShell></ProtectedRoute>} />
          <Route path="/training/team-lead-certification" element={<ProtectedRoute><AppShell><TeamLeadCertRoute /></AppShell></ProtectedRoute>} />
          <Route path="/admin/team-lead-certification" element={<ProtectedRoute requiredRole="Admin"><AppShell><TeamLeadCertAdmin /></AppShell></ProtectedRoute>} />
          <Route path="/training/onboarding" element={<ProtectedRoute><AppShell><Training /></AppShell></ProtectedRoute>} />
          <Route path="/training/lead-generation" element={<ProtectedRoute><AppShell><Training moduleKey="leadGeneration" /></AppShell></ProtectedRoute>} />
          <Route path="/training/open-houses" element={<ProtectedRoute><AppShell><Training moduleKey="openHouses" /></AppShell></ProtectedRoute>} />
          <Route path="/training/youtube" element={<ProtectedRoute><AppShell><Training moduleKey="youtube" /></AppShell></ProtectedRoute>} />
          <Route path="/training/farming" element={<ProtectedRoute><AppShell><Training moduleKey="farming" /></AppShell></ProtectedRoute>} />
          <Route path="/training/facebook-groups" element={<ProtectedRoute><AppShell><Training moduleKey="facebookGroups" /></AppShell></ProtectedRoute>} />
          <Route path="/training/expireds" element={<ProtectedRoute><AppShell><Training moduleKey="expireds" /></AppShell></ProtectedRoute>} />
          <Route path="/training/referrals-reviews" element={<ProtectedRoute><AppShell><Training moduleKey="referrals" /></AppShell></ProtectedRoute>} />
          <Route path="/training/sellers-listings" element={<ProtectedRoute><AppShell><Training moduleKey="sellersListings" /></AppShell></ProtectedRoute>} />
          <Route path="/training/lead-magnets" element={<ProtectedRoute><AppShell><Training moduleKey="leadMagnets" /></AppShell></ProtectedRoute>} />
          <Route path="/training/new-agents" element={<ProtectedRoute><AppShell><Training moduleKey="newAgents" /></AppShell></ProtectedRoute>} />
          <Route path="/training/ai-agents" element={<ProtectedRoute><AppShell><Training moduleKey="aiAgents" /></AppShell></ProtectedRoute>} />
          <Route path="/training/buyers" element={<ProtectedRoute><AppShell><Training moduleKey="buyers" /></AppShell></ProtectedRoute>} />
          <Route path="/training/fastcap" element={<ProtectedRoute><AppShell><Training moduleKey="fastcap" /></AppShell></ProtectedRoute>} />
          <Route path="/training/knowledge" element={<ProtectedRoute><AppShell><KnowledgeBase /></AppShell></ProtectedRoute>} />
          <Route path="/goals" element={<Navigate to="/profile" replace />} />
          {/* Leads is the CRM. Old /crm/* links redirect here. */}
          <Route path="/leads" element={<Navigate to="/leads/contacts" replace />} />
          <Route path="/leads/contacts" element={<ProtectedRoute><AppShell><CrmGate><ContactsPage /></CrmGate></AppShell></ProtectedRoute>} />
          <Route path="/leads/duplicates" element={<ProtectedRoute><AppShell><CrmGate><DuplicatesPage /></CrmGate></AppShell></ProtectedRoute>} />
          <Route path="/leads/email-builder" element={<ProtectedRoute><AppShell><CrmGate><EmailBuilderPage /></CrmGate></AppShell></ProtectedRoute>} />
          <Route path="/pipeline" element={<ProtectedRoute><AppShell><CrmGate><SellerInterestsPage /></CrmGate></AppShell></ProtectedRoute>} />
            <Route path="/pipeline/seller-interests" element={<Navigate to="/pipeline" replace />} />
            <Route path="/pipeline/buyers" element={<Navigate to="/pipeline?view=buyer" replace />} />
            <Route path="/leads/seller-interests" element={<Navigate to="/pipeline" replace />} />
            <Route path="/leads/lists" element={<ProtectedRoute><AppShell><CrmGate><ListsPage /></CrmGate></AppShell></ProtectedRoute>} />
            <Route path="/inbox" element={<ProtectedRoute><AppShell><CrmGate><InboxPage /></CrmGate></AppShell></ProtectedRoute>} />
            <Route path="/leads/messages-log" element={<ProtectedRoute><AppShell><CrmGate><MessagesLogPage /></CrmGate></AppShell></ProtectedRoute>} />
          <Route path="/leads/dialer" element={<ProtectedRoute><AppShell><CrmGate><PowerDialerPage /></CrmGate></AppShell></ProtectedRoute>} />
          <Route path="/leads/campaigns" element={<ProtectedRoute><AppShell><CrmGate><CampaignsPage /></CrmGate></AppShell></ProtectedRoute>} />
          <Route path="/crm" element={<Navigate to="/leads/contacts" replace />} />
          <Route path="/crm/contacts" element={<Navigate to="/leads/contacts" replace />} />
          <Route path="/crm/email-builder" element={<Navigate to="/leads/email-builder" replace />} />
          <Route path="/crm/lists" element={<Navigate to="/leads/lists" replace />} />
          <Route path="/crm/dialer" element={<Navigate to="/leads/dialer" replace />} />
          <Route path="/crm/campaigns" element={<Navigate to="/leads/campaigns" replace />} />
          <Route path="/calendar" element={<ProtectedRoute><AppShell><CalendarPage /></AppShell></ProtectedRoute>} />
          <Route path="/website" element={<ProtectedRoute><KellyOnly><AppShell><ComingSoon title="My Website" /></AppShell></KellyOnly></ProtectedRoute>} />
          <Route path="/pending-commission" element={<ProtectedRoute><AppShell><PendingCommission /></AppShell></ProtectedRoute>} />
          <Route path="/performance" element={<ProtectedRoute><AppShell><MyPerformance /></AppShell></ProtectedRoute>} />
          <Route path="/feedback" element={<ProtectedRoute><AppShell><Feedback /></AppShell></ProtectedRoute>} />
          <Route path="/tools" element={<ProtectedRoute><AppShell><ToolsHub /></AppShell></ProtectedRoute>} />
          <Route path="/tools/cma" element={<ProtectedRoute><AppShell><ToolRoleGate><CmaBuilder /></ToolRoleGate></AppShell></ProtectedRoute>} />
          <Route path="/tools/pocket-listings" element={<ProtectedRoute><AppShell><ToolRoleGate><PocketListings /></ToolRoleGate></AppShell></ProtectedRoute>} />
          <Route path="/tools/mortgage" element={<ProtectedRoute><AppShell><ToolRoleGate><MortgageCalculator /></ToolRoleGate></AppShell></ProtectedRoute>} />
          <Route path="/tools/market-update" element={<ProtectedRoute><AppShell><MarketUpdate /></AppShell></ProtectedRoute>} />
          <Route path="/tools/net-sheet" element={<ProtectedRoute><AppShell><ToolRoleGate><SellerNetSheet /></ToolRoleGate></AppShell></ProtectedRoute>} />
          <Route path="/tools/closing-costs" element={<ProtectedRoute><AppShell><ClosingCostEstimator /></AppShell></ProtectedRoute>} />
          <Route path="*" element={<Login />} />
        </Routes>
      </AuthProvider>
    </Router>
  );
}

export default App;
// Force rebuild Tue Oct  6 20:36:41 UTC 2026
